import { useQuery } from '@tanstack/react-query';
import {
  Package, CheckCircle, Users, Shield, AlertTriangle, RefreshCw,
} from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { assetsService, type AssetListItem } from '@/services/assets.service';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { StatCard } from '@/components/ui/stat-card';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDate } from '@/lib/dateFormat';
import { Notice } from '@/components/ui/notice';

function OverviewSkeleton() {
  return (
    <div className="space-y-5">
      <div className="h-8 w-48 rounded-lg bg-muted/60 animate-pulse" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-xl bg-card p-5 shadow-card animate-pulse">
            <div className="h-4 w-24 rounded bg-muted/60 mb-3" />
            <div className="h-8 w-16 rounded bg-muted/60" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function AssetsOverviewPage() {
  const statsQuery = useQuery({
    queryKey: ['assets', 'stats'],
    queryFn: () => assetsService.getStats(),
  });

  if (statsQuery.isLoading) return <OverviewSkeleton />;

  if (statsQuery.isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
        <p className="text-sm text-destructive">Failed to load asset overview.</p>
        <button
          onClick={() => statsQuery.refetch()}
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </button>
      </div>
    );
  }

  const stats = statsQuery.data;

  // Match by status_code (stable) rather than display name (mutable)
  const inStockCount = stats?.by_status?.find(
    (s) => s.status_code?.toLowerCase() === 'in_stock',
  )?.count ?? 0;

  const issuedCount = stats?.by_status?.find(
    (s) => s.status_code?.toLowerCase() === 'issued' || s.status_code?.toLowerCase() === 'assigned',
  )?.count ?? 0;

  // by_category shape: { category_id, category_name, count }
  const maxCategoryCount = Math.max(
    1,
    ...(stats?.by_category ?? []).map((c) => c.count),
  );

  const recentColumns: Column<AssetListItem>[] = [
    {
      key: 'asset_code',
      header: 'Asset Code',
      cell: (r) => <span className="font-mono text-xs font-medium">{r.asset_code}</span>,
    },
    {
      key: 'category',
      header: 'Category',
      cell: (r) => r.category?.name ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'brand_model',
      header: 'Brand / Model',
      cell: (r) => {
        const parts = [r.brand?.name, r.model_name].filter(Boolean).join(' / ');
        return parts || <span className="text-muted-foreground">—</span>;
      },
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) =>
        r.status ? (
          <span className="inline-flex items-center gap-1.5">
            <span
              className="h-2 w-2 rounded-full shrink-0"
              style={{ backgroundColor: r.status.color }}
            />
            <span className="text-sm">{r.status.name}</span>
          </span>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        ),
    },
    {
      key: 'assigned_to',
      header: 'Assigned To',
      cell: (r) => r.assigned_to?.name ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'date',
      header: 'Date',
      cell: (r) => <span className="text-muted-foreground">{formatDate(r.created_at)}</span>,
    },
  ];

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-semibold text-foreground">Assets Overview</h1>

      <PermissionGate permission={PERMISSIONS.ASSETS_OVERVIEW}>
        <>
          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <StatCard
              label="Total Assets"
              value={stats?.total ?? '—'}
              tone="sky"
              icon={Package}
            />
            <StatCard
              label="In Stock"
              value={inStockCount}
              tone="emerald"
              icon={CheckCircle}
            />
            <StatCard
              label="Issued"
              value={issuedCount}
              tone="violet"
              icon={Users}
            />
            <StatCard
              label="Under Warranty"
              value={stats?.under_warranty ?? '—'}
              tone="amber"
              icon={Shield}
            />
          </div>

          {/* Middle row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {/* Assets by Status */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Assets by Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(stats?.by_status ?? []).length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No data</p>
                ) : (
                  (stats?.by_status ?? []).map((item) => (
                    <div key={item.status_id} className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="h-2.5 w-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: item.color ?? '#94a3b8' }}
                        />
                        <span className="text-sm truncate">{item.status_name}</span>
                      </div>
                      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground shrink-0">
                        {item.count}
                      </span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Assets by Category */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Assets by Category</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(stats?.by_category ?? []).length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No data</p>
                ) : (
                  (stats?.by_category ?? []).map((item) => (
                    <div key={item.category_id} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm truncate">{item.category_name}</span>
                        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground shrink-0 ml-2">
                          {item.count}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary/60 transition-all"
                          style={{ width: `${Math.round((item.count / maxCategoryCount) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Warranty expiry warning */}
          {(stats?.warranty_expiring_soon ?? 0) > 0 && (
            <Notice tone="warning" icon={AlertTriangle} compact className="mt-4">
              {stats!.warranty_expiring_soon} asset{stats!.warranty_expiring_soon !== 1 ? 's' : ''} have warranty expiring within 30 days
            </Notice>
          )}

          {/* Recent Assets */}
          <div className="mt-4">
            <div className="mb-3">
              <h2 className="text-base font-semibold text-foreground">Recent Assets</h2>
            </div>
            <DataTable
              columns={recentColumns}
              rows={stats?.recent ?? []}
              rowKey={(r) => r.id}
              loading={statsQuery.isLoading}
              error={statsQuery.isError}
              onRetry={() => statsQuery.refetch()}
              empty="No assets found."
            />
          </div>
        </>
      </PermissionGate>
    </div>
  );
}
